import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, Switch } from 'react-native';
import { Text, Card, Button, ActivityIndicator, Divider, Chip, Modal, Portal, RadioButton } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import * as FileSystem from 'expo-file-system/legacy';
import api from '../../services/api';
import { generateCotizacionHTML } from '../../utils/facturaGenerator';

const ESTADOS_CONFIG = {
  vigente:    { label: 'Vigente',    color: '#28A745', icon: 'checkmark-circle' },
  vencida:    { label: 'Vencida',    color: '#FFC107', icon: 'time' },
  convertida: { label: 'Convertida', color: '#17A2B8', icon: 'swap-horizontal' },
  cancelada:  { label: 'Cancelada',  color: '#DC3545', icon: 'close-circle' },
};

const DetalleCotizacionScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { cotizacion: cotParam } = route.params;

  const [loading, setLoading] = useState(true);
  const [cotizacion, setCotizacion] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Opciones del modal de conversión a venta
  const [convertModalVisible, setConvertModalVisible] = useState(false);
  const [convertOpts, setConvertOpts] = useState({
    venta_directa: false,
    metodo_pago: 'efectivo',
    dias_credito: 8,
  });

  const loadDetalle = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getCotizacion(cotParam.id);
      setCotizacion(data);
    } catch (error) {
      console.error('Error al cargar cotizacion:', error);
      Alert.alert('Error', 'No se pudo cargar el detalle');
    } finally {
      setLoading(false);
    }
  }, [cotParam.id]);

  useEffect(() => {
    loadDetalle();
  }, [loadDetalle]);

  const formatCurrency = (value) => `L. ${parseFloat(value || 0).toFixed(2)}`;

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    return new Date(fecha).toLocaleDateString('es-HN', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const isVencida = () => {
    if (!cotizacion || cotizacion.estado !== 'vigente') return false;
    if (!cotizacion.fecha_vencimiento) return false;
    return new Date(cotizacion.fecha_vencimiento) < new Date();
  };

  const openConvertModal = () => {
    setConvertOpts({ venta_directa: false, metodo_pago: 'efectivo', dias_credito: 8 });
    setConvertModalVisible(true);
  };

  const handleConvertir = () => {
    if (isVencida()) {
      Alert.alert('Cotizacion Vencida', 'Esta cotizacion ya vencio. ¿Desea convertirla de todas formas?', [
        { text: 'Cancelar', style: 'cancel' },
        { text: 'Continuar', onPress: () => openConvertModal() },
      ]);
    } else {
      openConvertModal();
    }
  };

  const doConvertir = async () => {
    try {
      setProcessing(true);
      const payload = {
        metodo_pago: convertOpts.metodo_pago,
        venta_directa: convertOpts.venta_directa,
      };
      if (convertOpts.metodo_pago === 'credito') {
        payload.dias_credito = convertOpts.dias_credito;
      }
      const result = await api.convertirCotizacion(cotParam.id, payload);
      setConvertModalVisible(false);
      Alert.alert(
        'Convertida',
        `Venta #${result.venta_id} creada exitosamente`,
        [{ text: 'OK', onPress: () => navigation.goBack() }]
      );
    } catch (error) {
      Alert.alert('Error', error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelar = () => {
    Alert.alert(
      'Cancelar Cotizacion',
      `¿Esta seguro de cancelar la cotizacion ${cotizacion.numero}?`,
      [
        { text: 'No', style: 'cancel' },
        {
          text: 'Si, Cancelar',
          style: 'destructive',
          onPress: async () => {
            try {
              setProcessing(true);
              await api.cancelarCotizacion(cotParam.id);
              Alert.alert('Cancelada', 'Cotizacion cancelada', [
                { text: 'OK', onPress: () => navigation.goBack() }
              ]);
            } catch (error) {
              Alert.alert('Error', error.message);
            } finally {
              setProcessing(false);
            }
          }
        }
      ]
    );
  };

  const handleCompartirPDF = async () => {
    if (!cotizacion) return;
    try {
      // Cargar configuración del negocio (logo, nombre, etc.)
      let config = {};
      try { config = await api.getConfiguracion(); } catch (e) { /* defaults */ }

      // Fallback a logo local si la config no trae uno
      if (!config.logo_url) {
        try {
          const logoPath = FileSystem.documentDirectory + 'logo.png';
          const logoExists = await FileSystem.getInfoAsync(logoPath);
          if (logoExists.exists) {
            const base64 = await FileSystem.readAsStringAsync(logoPath, {
              encoding: FileSystem.EncodingType.Base64,
            });
            config.logo_url = `data:image/png;base64,${base64}`;
          }
        } catch (logoError) {
          // Sin logo — el PDF se genera con solo texto
        }
      }

      const html = generateCotizacionHTML(cotizacion, config);
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri);
      }
    } catch (error) {
      Alert.alert('Error', 'No se pudo generar el PDF');
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007BFF" />
        <Text style={styles.loadingText}>Cargando cotizacion...</Text>
      </View>
    );
  }

  if (!cotizacion) {
    return (
      <View style={styles.loadingContainer}>
        <Text>No se pudo cargar la cotizacion</Text>
        <Button mode="contained" onPress={() => navigation.goBack()} style={{ marginTop: 16 }}>Volver</Button>
      </View>
    );
  }

  const vencida = isVencida();
  const estadoKey = vencida ? 'vencida' : cotizacion.estado;
  const estadoConfig = ESTADOS_CONFIG[estadoKey] || ESTADOS_CONFIG.vigente;

  return (
    <ScrollView style={styles.container}>
      {/* Estado y numero */}
      <Card style={[styles.card, { borderLeftWidth: 4, borderLeftColor: estadoConfig.color }]}>
        <Card.Content>
          <View style={styles.headerRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.numero}>{cotizacion.numero}</Text>
              <Text style={styles.fecha}>Creada: {formatFecha(cotizacion.fecha)}</Text>
            </View>
            <Chip
              style={[styles.estadoChip, { backgroundColor: estadoConfig.color }]}
              textStyle={{ color: '#fff', fontWeight: 'bold', fontSize: 11 }}
              icon={() => <Ionicons name={estadoConfig.icon} size={16} color="#fff" />}
            >
              {estadoConfig.label}
            </Chip>
          </View>
          {cotizacion.fecha_vencimiento && (
            <View style={[styles.vigenciaBar, vencida && { backgroundColor: '#F8D7DA' }]}>
              <Ionicons name="time" size={16} color={vencida ? '#DC3545' : '#856404'} />
              <Text style={[styles.vigenciaText, vencida && { color: '#DC3545' }]}>
                {vencida ? 'VENCIDA' : 'Vigente hasta'}: {formatFecha(cotizacion.fecha_vencimiento)}
              </Text>
            </View>
          )}
        </Card.Content>
      </Card>

      {/* Cliente */}
      <Card style={styles.card}>
        <Card.Title title="Cliente" left={() => <Ionicons name="person" size={24} color="#666" />} />
        <Card.Content>
          <Text style={styles.clienteNombre}>{cotizacion.cliente_nombre || 'Sin cliente'}</Text>
          {cotizacion.cliente_telefono && (
            <Text style={styles.clienteDetalle}><Ionicons name="call" size={14} color="#888" /> {cotizacion.cliente_telefono}</Text>
          )}
          {cotizacion.cliente_direccion && (
            <Text style={styles.clienteDetalle}><Ionicons name="location" size={14} color="#888" /> {cotizacion.cliente_direccion}</Text>
          )}
          <Text style={styles.clienteDetalle}><Ionicons name="business" size={14} color="#888" /> Vendedor: {cotizacion.vendedor_nombre || 'N/A'}</Text>
        </Card.Content>
      </Card>

      {/* Productos */}
      <Card style={styles.card}>
        <Card.Title title={`Productos (${(cotizacion.items || []).length})`} left={() => <Ionicons name="list" size={24} color="#666" />} />
        <Card.Content>
          {(cotizacion.items || []).map((item, index) => (
            <View key={item.id || index}>
              <View style={styles.productoRow}>
                <View style={{ flex: 1, marginRight: 8 }}>
                  <Text style={styles.productoNombre}>{item.producto_nombre || 'Producto'}</Text>
                  <View style={{ flexDirection: 'row', marginTop: 2 }}>
                    {item.marca && <Text style={styles.productoMarca}>{item.marca}</Text>}
                    {item.producto_codigo && <Text style={styles.productoCodigo}>{item.producto_codigo}</Text>}
                  </View>
                </View>
                <View style={{ alignItems: 'center', marginRight: 12 }}>
                  <Text style={styles.cantidadNum}>{parseFloat(item.cantidad)}</Text>
                  <Text style={styles.cantidadUd}>{item.unidad_nombre || 'ud'}</Text>
                </View>
                <Text style={styles.subtotal}>{formatCurrency(item.subtotal)}</Text>
              </View>
              {index < (cotizacion.items || []).length - 1 && <Divider style={{ marginVertical: 6 }} />}
            </View>
          ))}

          <Divider style={{ marginVertical: 10 }} />
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>TOTAL</Text>
            <Text style={styles.totalValue}>{formatCurrency(cotizacion.total)}</Text>
          </View>
        </Card.Content>
      </Card>

      {/* Notas */}
      {cotizacion.notas && (
        <Card style={styles.card}>
          <Card.Content>
            <Text style={styles.notasLabel}>Notas:</Text>
            <Text style={styles.notasText}>{cotizacion.notas}</Text>
          </Card.Content>
        </Card>
      )}

      {/* Acciones */}
      <Card style={styles.card}>
        <Card.Content>
          <Button
            mode="contained"
            icon="share"
            onPress={handleCompartirPDF}
            style={[styles.actionBtn, { backgroundColor: '#007BFF' }]}
          >
            Compartir PDF
          </Button>

          {cotizacion.estado === 'vigente' && (
            <>
              <Button
                mode="contained"
                icon="swap-horizontal"
                onPress={handleConvertir}
                style={[styles.actionBtn, { backgroundColor: '#28A745' }]}
                loading={processing}
                disabled={processing}
              >
                Convertir a Venta
              </Button>
              <Button
                mode="outlined"
                icon="close-circle"
                onPress={handleCancelar}
                style={styles.actionBtn}
                textColor="#DC3545"
                loading={processing}
                disabled={processing}
              >
                Cancelar Cotizacion
              </Button>
            </>
          )}

          {cotizacion.estado === 'convertida' && cotizacion.venta_id && (
            <View style={styles.convertidaInfo}>
              <Ionicons name="swap-horizontal-outline" size={20} color="#17A2B8" />
              <Text style={styles.convertidaText}>Convertida a Venta #{cotizacion.venta_id}</Text>
            </View>
          )}
        </Card.Content>
      </Card>

      <Portal>
        <Modal
          visible={convertModalVisible}
          onDismiss={() => !processing && setConvertModalVisible(false)}
          contentContainerStyle={styles.modalContainer}
        >
          <ScrollView>
            <Text style={styles.modalTitle}>Convertir a Venta</Text>
            <Text style={styles.modalHint}>Cotización {cotizacion.numero}</Text>

            {/* Tipo de entrega */}
            <View style={styles.modalSection}>
              <View style={styles.tipoRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.tipoTitle}>
                    {convertOpts.venta_directa ? 'Venta en Mostrador' : 'Venta con Entrega'}
                  </Text>
                  <Text style={styles.tipoSubtitle}>
                    {convertOpts.venta_directa
                      ? 'El cliente se lleva el producto al instante. No pasa por bodega ni ruta.'
                      : 'El pedido va a bodega y luego sale a ruta para entregarse.'}
                  </Text>
                </View>
                <Switch
                  value={convertOpts.venta_directa}
                  onValueChange={(val) => setConvertOpts(prev => ({ ...prev, venta_directa: val }))}
                />
              </View>
            </View>

            <Divider style={{ marginVertical: 12 }} />

            {/* Método de pago */}
            <Text style={styles.modalSectionLabel}>Método de Pago</Text>
            <RadioButton.Group
              onValueChange={(value) => setConvertOpts(prev => ({ ...prev, metodo_pago: value }))}
              value={convertOpts.metodo_pago}
            >
              <View style={styles.radioRow}>
                <RadioButton value="efectivo" />
                <Text style={styles.radioLabel}>Efectivo</Text>
              </View>
              <View style={styles.radioRow}>
                <RadioButton value="tarjeta" />
                <Text style={styles.radioLabel}>Tarjeta</Text>
              </View>
              <View style={styles.radioRow}>
                <RadioButton value="transferencia" />
                <Text style={styles.radioLabel}>Transferencia</Text>
              </View>
              <View style={styles.radioRow}>
                <RadioButton value="credito" />
                <Text style={styles.radioLabel}>Crédito</Text>
              </View>
            </RadioButton.Group>

            {/* Plazo de crédito */}
            {convertOpts.metodo_pago === 'credito' && (
              <View style={styles.plazoSection}>
                <Text style={styles.modalSectionLabel}>Plazo de Crédito</Text>
                <View style={styles.plazoRow}>
                  {[8, 15, 22].map(dias => (
                    <Button
                      key={dias}
                      mode={convertOpts.dias_credito === dias ? 'contained' : 'outlined'}
                      onPress={() => setConvertOpts(prev => ({ ...prev, dias_credito: dias }))}
                      style={styles.plazoBtn}
                      compact
                    >
                      {dias} días
                    </Button>
                  ))}
                </View>
              </View>
            )}

            <Divider style={{ marginVertical: 12 }} />

            <View style={styles.modalActions}>
              <Button
                mode="outlined"
                onPress={() => setConvertModalVisible(false)}
                disabled={processing}
                style={{ flex: 1, marginRight: 6 }}
              >
                Cancelar
              </Button>
              <Button
                mode="contained"
                onPress={doConvertir}
                loading={processing}
                disabled={processing}
                style={{ flex: 1, marginLeft: 6, backgroundColor: '#28A745' }}
              >
                Convertir
              </Button>
            </View>
          </ScrollView>
        </Modal>
      </Portal>

      <View style={{ height: 30 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5', padding: 8 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#666' },
  card: { marginBottom: 8, elevation: 2 },
  // Header
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  numero: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  fecha: { fontSize: 13, color: '#888', marginTop: 2 },
  estadoChip: { marginLeft: 8 },
  vigenciaBar: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFF3CD', padding: 8, borderRadius: 6, marginTop: 10 },
  vigenciaText: { fontSize: 13, color: '#856404', marginLeft: 6, fontWeight: '500' },
  // Cliente
  clienteNombre: { fontSize: 16, fontWeight: 'bold', color: '#333', marginBottom: 6 },
  clienteDetalle: { fontSize: 13, color: '#666', marginBottom: 4 },
  // Productos
  productoRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8 },
  productoNombre: { fontSize: 14, fontWeight: '600', color: '#333' },
  productoMarca: { fontSize: 12, color: '#17A2B8', marginRight: 8 },
  productoCodigo: { fontSize: 12, color: '#999' },
  cantidadNum: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  cantidadUd: { fontSize: 10, color: '#888' },
  subtotal: { fontSize: 14, fontWeight: '600', color: '#28A745', minWidth: 70, textAlign: 'right' },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  totalLabel: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  totalValue: { fontSize: 20, fontWeight: 'bold', color: '#28A745' },
  // Notas
  notasLabel: { fontSize: 14, fontWeight: '600', color: '#333', marginBottom: 4 },
  notasText: { fontSize: 13, color: '#666' },
  // Acciones
  actionBtn: { marginBottom: 8 },
  convertidaInfo: { flexDirection: 'row', alignItems: 'center', padding: 12, backgroundColor: '#D1ECF1', borderRadius: 8 },
  convertidaText: { fontSize: 14, color: '#0C5460', marginLeft: 8, fontWeight: '600' },
  // Modal de conversión
  modalContainer: { backgroundColor: '#fff', margin: 20, padding: 20, borderRadius: 10, maxHeight: '90%' },
  modalTitle: { fontSize: 20, fontWeight: 'bold', color: '#333' },
  modalHint: { fontSize: 13, color: '#888', marginBottom: 14 },
  modalSection: { marginTop: 4 },
  modalSectionLabel: { fontSize: 14, fontWeight: '600', color: '#333', marginBottom: 8 },
  tipoRow: { flexDirection: 'row', alignItems: 'center' },
  tipoTitle: { fontSize: 15, fontWeight: '600', color: '#333' },
  tipoSubtitle: { fontSize: 12, color: '#666', marginTop: 2, marginRight: 12 },
  radioRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 2 },
  radioLabel: { fontSize: 14, color: '#333' },
  plazoSection: { marginTop: 12 },
  plazoRow: { flexDirection: 'row', justifyContent: 'space-between' },
  plazoBtn: { flex: 1, marginHorizontal: 4 },
  modalActions: { flexDirection: 'row', marginTop: 4 },
});

export default DetalleCotizacionScreen;
